/**
 * Blog API Service
 * 
 * All blog-related API endpoints following the API v2.0 specification.
 * Request bodies are sent directly (no data wrapper).
 */

import { get, post, put, del } from '../../utils/httpClient';
import logger from '../../utils/logger';

/**
 * Get all blog posts (including non-visible ones)
 */
export const fetchAllBlogPosts = async () => {
  try {
    const response = await get('/blog/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch all blog posts', error);
    throw error;
  }
};

/**
 * Get only visible (public) blog posts
 */
export const fetchPublicBlogPosts = async () => {
  try {
    const response = await get('/blog/list/public');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch public blog posts', error);
    throw error;
  }
};

/**
 * Get a single blog post by ID
 */
export const fetchBlogPostById = async (id) => {
  try {
    if (!id) {
      throw new Error('Blog post ID is required');
    }
    const response = await post('/blog/view', { id });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch blog post by ID', error, { id });
    throw error;
  }
};

/**
 * Get likes count for a blog post
 */
export const fetchBlogPostLikes = async (id) => {
  try {
    if (!id) {
      throw new Error('Blog post ID is required');
    }
    const response = await post('/blog/likes', { id });
    return response.data?.[0]?.likes || 0;
  } catch (error) {
    logger.error('Failed to fetch blog post likes', error, { id });
    throw error;
  }
};

/**
 * Increment likes for a blog post
 */
export const likeBlogPost = async (id) => {
  try {
    if (!id) {
      throw new Error('Blog post ID is required');
    }
    const response = await post('/blog/like', { id });
    return response;
  } catch (error) {
    logger.error('Failed to like blog post', error, { id });
    throw error;
  }
};

/**
 * Decrement likes for a blog post
 */
export const unlikeBlogPost = async (id) => {
  try {
    if (!id) {
      throw new Error('Blog post ID is required');
    }
    const response = await post('/blog/unlike', { id });
    return response;
  } catch (error) {
    logger.error('Failed to unlike blog post', error, { id });
    throw error;
  }
};

/**
 * Get all comments for a blog post
 */
export const fetchBlogPostComments = async (id) => {
  try {
    if (!id) {
      throw new Error('Blog post ID is required');
    }
    const response = await post('/blog/comments', { id });
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch blog post comments', error, { id });
    throw error;
  }
};

/**
 * Add a comment to a blog post
 */
export const addBlogPostComment = async (commentData) => {
  try {
    const { blogpost_id, author_name, author_email, text, reply_to } = commentData;
    
    if (!blogpost_id || !author_name || !author_email || !text) {
      throw new Error('Missing required fields: blogpost_id, author_name, author_email, text');
    }

    const response = await post('/blog/comment/add', {
      blogpost_id,
      author_name,
      author_email,
      text,
      reply_to: reply_to || null,
    });
    
    return response.data;
  } catch (error) {
    logger.error('Failed to add blog post comment', error, { commentData });
    throw error;
  }
};

/**
 * Delete a comment
 */
export const deleteBlogPostComment = async (id) => {
  try {
    if (!id) {
      throw new Error('Comment ID is required');
    }
    const response = await del('/blog/comment/delete', {
      data: { id },
    });
    return response;
  } catch (error) {
    logger.error('Failed to delete blog post comment', error, { id });
    throw error;
  }
};

/**
 * Get all blog post categories
 */
export const fetchAllBlogCategories = async () => {
  try {
    const response = await get('/blog/categories/list');
    return response.data || [];
  } catch (error) {
    logger.error('Failed to fetch blog categories', error);
    throw error;
  }
};

/**
 * Get a single blog post category by ID
 */
export const fetchBlogCategoryById = async (id) => {
  try {
    if (!id) {
      throw new Error('Category ID is required');
    }
    const response = await post('/blog/category', { id });
    return response.data;
  } catch (error) {
    logger.error('Failed to fetch blog category by ID', error, { id });
    throw error;
  }
};

